import { Trans, plural, t } from "@web-monorepo/i18n/macro";
import {
  DDSBodyText,
  DDSButton,
  DDSDialog,
  DDSDialogBody,
  DDSDialogContent,
  DDSDialogFooter,
  DDSDialogHeader,
  DDSHeadlineText,
  DDSTextField,
} from "@web-monorepo/dds";
import { keyBy } from "es-toolkit/compat";
import React from "react";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import { appMetadataGroups, Group } from "src/pods/groups";
import createGroups, { GroupRestriction } from "src/pods/groups/createGroups";
import { Student } from "src/pods/students";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import computeOptimalBaseAvatarURL from "src/utils/computeOptimalBaseAvatarURL";
import logEvent from "src/utils/logEvent";
import GroupRestrictions from "./GroupRestrictions";
import GroupsDisplay from "./GroupsDisplay";
import styles from "./GroupsRemote.module.css";

const GROUP_OPTIONS = [{ size: 2 }, { size: 3 }, { size: 4 }, { size: 5 }, { size: 6 }];

type GroupsRemoteProps = {
  classId: string;
  mountPoint: string;
  displayOpen: (url: string, action?: ToolkitAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  students: Student[] | null;
  editGroupRestrictions: ({
    classId,
    groupOfStudentIdGroups,
  }: {
    classId: string;
    groupOfStudentIdGroups: GroupRestriction[];
  }) => void;
  groupRestrictions: GroupRestriction[];
};

type GroupsRemoteState = {
  selectedGroupOption: { size: number } | null;
  selectedGroups: Group[] | null;
  askForGroupSize: boolean;
  sizeInput?: number;
  askForGroupRestrictions: boolean;
  baseAvatarURL?: string;
  errored: boolean;
};

const initialState: GroupsRemoteState = {
  selectedGroupOption: null,
  selectedGroups: null,
  askForGroupSize: false,
  sizeInput: undefined,
  askForGroupRestrictions: false,
  errored: false,
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class GroupsRemote extends React.Component<GroupsRemoteProps, GroupsRemoteState> {
  constructor(props: GroupsRemoteProps) {
    super(props);
    this.state = initialState;
  }

  componentDidMount() {
    logEvent({
      eventName: "groupcreator.setup.open",
    });
  }

  componentDidUpdate(prevProps: GroupsRemoteProps) {
    if (this.props.students && !prevProps.students && this.state.selectedGroupOption && !this.state.selectedGroups) {
      this.openDisplay(this.state.selectedGroupOption);
    }
  }

  componentWillUnmount() {
    logEvent({
      eventName: "groupcreator.setup.close",
    });
  }

  render() {
    const { students, groupRestrictions, classId, mountPoint } = this.props;
    const { sizeInput, askForGroupSize, selectedGroups, askForGroupRestrictions, baseAvatarURL, errored } = this.state;

    if (!students || !groupRestrictions) {
      return (
        <div className={styles.loadingContainer}>
          <Trans>Please wait...</Trans>
        </div>
      );
    }

    if (Array.isArray(students) && students.length === 0) {
      return (
        <AppPage {...appMetadataGroups()} classId={classId} mountPoint={mountPoint}>
          <DDSBodyText as="p" className={styles.errorContainer}>
            <Trans>Your class doesn't have any students :(</Trans>
          </DDSBodyText>
        </AppPage>
      );
    }

    function renderGroupOptions(this: GroupsRemote, groupOption: { size: number }) {
      return (
        <li key={groupOption.size} className={styles.listItem}>
          <DDSButton
            variant="unstyled"
            data-name="groups_remot:render_group_options:students_per_group"
            aria-label={plural(groupOption.size, {
              one: "# student per group",
              other: "# students per group",
            })}
            className={styles.optionButton}
            onClick={() => this.openDisplay(groupOption)}
          >
            {groupOption.size}
          </DDSButton>
        </li>
      );
    }

    return (
      <AppPage
        {...appMetadataGroups()}
        classId={classId}
        mountPoint={mountPoint}
        disableFocusTrap={this.state.askForGroupSize || this.state.askForGroupRestrictions || this.state.errored}
      >
        <article className={styles.app}>
          <header>
            <DDSHeadlineText as="h1" level="md" className={styles.title}>
              <Trans>How many students per group?</Trans>
            </DDSHeadlineText>
          </header>
          <section className={styles.groups}>
            <ul className={styles.list}>
              {GROUP_OPTIONS.map(renderGroupOptions, this)}
              <li className={styles.listItem}>
                <DDSButton
                  variant="unstyled"
                  data-name="groups_remot:add_custom_group_number"
                  className={styles.optionButton}
                  onClick={this.openGroupSizeInput}
                  aria-label={t`Add custom group number`}
                >
                  +
                </DDSButton>
              </li>
            </ul>
            <DDSButton
              size="small"
              variant="tertiary"
              data-name="groups_remote:render:group_maker_restrictions_open_modal_label"
              onClick={this.openGroupRestrictions}
              className={styles.dontGroupTogetherButton}
            >
              <Trans>Don't group together &gt;</Trans>
            </DDSButton>
          </section>
          {askForGroupSize && (
            <DDSDialog data-name="groups_remote:custom_size_dialog" open>
              <DDSDialogContent data-name="groups_remote:custom_size_dialog:content" onClose={this.closeGroupSizeInput}>
                <DDSDialogHeader visuallyHideTitle title={t`Custom group size`} onClose={this.closeGroupSizeInput} />
                <DDSDialogBody>
                  <DDSTextField
                    data-name="groups_remote:custom_size_input"
                    label={t`Add custom group number`}
                    type="number"
                    placeholder="7"
                    value={this.state.sizeInput?.toString() ?? ""}
                    onChange={this.onSizeChange}
                  />
                </DDSDialogBody>
                <DDSDialogFooter>
                  <DDSButton variant="tertiary" data-name="groups_remote:cancel" onClick={this.closeGroupSizeInput}>
                    <Trans>Cancel</Trans>
                  </DDSButton>
                  <DDSButton
                    data-name="groups_remote:done"
                    disabled={!sizeInput || sizeInput < 1}
                    onClick={() => {
                      if (!sizeInput) return;
                      this.closeGroupSizeInput();
                      this.openDisplay({ size: sizeInput });
                    }}
                  >
                    <Trans>Done</Trans>
                  </DDSButton>
                </DDSDialogFooter>
              </DDSDialogContent>
            </DDSDialog>
          )}
          {selectedGroups && mountPoint === "mobile" && (
            <AppModal
              {...appMetadataGroups()}
              onRequestHide={() => {
                this.props.displayClose();
                this.props.remoteClose();
              }}
              onRequestStart={() => this.props.displayOpen("/apps/display/groups")}
              onRequestStop={() => this.props.displayClose()}
            >
              <GroupsDisplay baseAvatarURL={baseAvatarURL} groups={selectedGroups} />
            </AppModal>
          )}
          {askForGroupRestrictions && (
            <GroupRestrictions
              classId={this.props.classId}
              editGroupRestrictions={this.props.editGroupRestrictions}
              onRequestHide={this.closeGroupRestrictions}
              students={students}
              groupRestrictions={groupRestrictions}
            />
          )}
          {errored && (
            <DDSDialog data-name="groups_remote:error_dialog" open>
              <DDSDialogContent data-name="groups_remote:error_dialog:content" onClose={this.dismissError}>
                <DDSDialogHeader title={t`Could not create groups`} onClose={this.dismissError} />
                <DDSDialogBody>
                  <Trans>Please try a different group size or remove students from 'Don't group together'.</Trans>
                </DDSDialogBody>
                <DDSDialogFooter>
                  <DDSButton data-name="groups_remote:ack_error" onClick={this.dismissError}>
                    <Trans>OK</Trans>
                  </DDSButton>
                </DDSDialogFooter>
              </DDSDialogContent>
            </DDSDialog>
          )}
        </article>
      </AppPage>
    );
  }

  openGroupSizeInput = () => {
    this.setState({ askForGroupSize: true, sizeInput: undefined });
  };

  closeGroupSizeInput = () => {
    this.setState({ askForGroupSize: false });
  };

  onSizeChange = (value: string) => {
    const sizeInput = parseInt(value, 10);

    if (sizeInput <= 0) {
      this.setState({
        sizeInput: 1,
      });

      return;
    }

    this.setState({ sizeInput });
  };

  openDisplay = (groupOption: { size: number }) => {
    const { students, groupRestrictions } = this.props;

    if (!students) {
      this.setState({ errored: true });
      return;
    }

    const baseAvatarURL = computeOptimalBaseAvatarURL(students.map((s) => s.avatar));

    const simplifiedStudents = keyBy(
      students.map((student) => ({
        _id: student._id,
        firstName: student.firstName,
        lastName: student.lastName,
        avatar: student.avatar.replace(baseAvatarURL!, ""),
      })),
      "_id",
    );

    const studentIds = students.map((s) => s._id);

    let groupsOfIds;
    try {
      groupsOfIds = createGroups(studentIds, groupOption.size, groupRestrictions);
      // eslint-disable-next-line no-catch-all/no-catch-all
    } catch {
      this.setState({ errored: true });
      return;
    }

    const groups = groupsOfIds.map((group) => ({ students: group.map((id) => simplifiedStudents[id]) }));

    this.setState({
      errored: false,
      selectedGroupOption: groupOption,
      selectedGroups: groups,
      baseAvatarURL: baseAvatarURL!,
    });

    this.props.displayOpen("/apps/display/groups", {
      type: "RECEIVE_GROUPS",
      payload: { groups, baseAvatarURL },
    });

    logEvent({
      eventName: GROUP_OPTIONS.includes(groupOption) ? "groupcreator.start" : "groupcreator.start.custom",
      eventValue: String(groupOption.size),
    });
  };

  closeDisplay = () => {
    this.setState({ selectedGroupOption: null, selectedGroups: null });
    this.props.displayClose();
  };

  openGroupRestrictions = () => {
    this.setState({ askForGroupRestrictions: true });
  };

  closeGroupRestrictions = () => {
    this.setState({ askForGroupRestrictions: false });
  };

  dismissError = () => {
    this.setState({ errored: false });
  };
}
